/**
 * Document `<head>` mutation helpers (#1716).
 *
 * The Senviro SPA ships a single static `index.html`, so every route used
 * to share one route-agnostic `<title>` + `<meta name="description">`
 * (Lighthouse SEO capped at 83 on all 33 pages). These helpers let a
 * route set its own metadata at runtime by updating the existing tags
 * **in place** — they never append a second `<meta name="description">`
 * or `<link rel="canonical">`, so repeated navigation can't accumulate
 * duplicate head tags.
 *
 * SSR-agnostic: this is a client SPA, so mutating `document.head` on
 * route change is the whole fix — no server render or hydration step is
 * involved. Every function is a no-op when `document` is undefined.
 */

/**
 * Count of `<RouteMeta>` instances that currently own the title. When > 0
 * the fallback `<DocumentTitle>` (mounted in AppShell) must NOT overwrite
 * `document.title` with the bare product name — otherwise, because its
 * effect runs after the route page's effect in commit order, it would
 * clobber the richer `<page> · <product>` title a route just set. A
 * counter (not a boolean) tolerates brief mount/unmount overlap during
 * route transitions.
 */
let routeTitleOwners = 0;

/** Internal: a `<RouteMeta>` has taken ownership of the document title. */
export function acquireRouteTitleOwnership(): void {
  routeTitleOwners += 1;
}

/** Internal: a `<RouteMeta>` has released ownership of the document title. */
export function releaseRouteTitleOwnership(): void {
  routeTitleOwners = Math.max(0, routeTitleOwners - 1);
}

/** True while at least one `<RouteMeta>` owns the title. */
export function isRouteTitleOwned(): boolean {
  return routeTitleOwners > 0;
}

/** Set (or clear) the browser tab + SEO title. */
export function setDocumentTitle(title: string): void {
  if (typeof document === 'undefined') return;
  document.title = title;
}

/**
 * Upsert `<meta name="description" content="…">`. Creates the tag on
 * first use, then updates the same element on every later call so the
 * head never grows a duplicate. Passing an empty string removes the
 * managed tag (so a route without a description doesn't inherit the
 * previous route's text).
 */
export function setMetaDescription(description: string): void {
  if (typeof document === 'undefined') return;
  const selector = 'meta[name="description"]';
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (!description) {
    // Only remove a tag we manage — leave any author-authored static one.
    if (existing?.dataset.managed === 'route-meta') existing.remove();
    return;
  }
  let tag = existing;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    tag.dataset.managed = 'route-meta';
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', description);
}

/**
 * Upsert `<link rel="canonical" href="…">`. Same update-in-place
 * contract as {@link setMetaDescription}. Passing an empty string
 * removes the managed canonical link.
 */
export function setCanonicalLink(href: string): void {
  if (typeof document === 'undefined') return;
  const selector = 'link[rel="canonical"]';
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  if (!href) {
    if (existing?.dataset.managed === 'route-meta') existing.remove();
    return;
  }
  let tag = existing;
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    tag.dataset.managed = 'route-meta';
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/**
 * Format a tab/SEO title as `<page> · <product>`, collapsing to just the
 * product name when the page title is empty (or equal to the product).
 */
export function formatDocumentTitle(
  pageTitle: string | undefined,
  productName: string,
): string {
  const page = pageTitle?.trim();
  if (!page || page === productName) return productName;
  return `${page} · ${productName}`;
}
